// plan-chrome — the plan block beside the editor on the isolated guided-make and assignment
// surfaces (plan-first-activity, Trello 5mlpF1aX).
//
// The NINTH sibling chrome module, and deliberately NOT one of the eight uniformly-wired ones:
// it ALLOW-LISTS `body[data-surface]` to exactly `guided-make | assignment` (the only two hosts a
// plan block can attach to). Every other surface — review, challengeFeedback, viewonly,
// trace-capture and above all the default `editor` — returns before touching anything. The list
// is pinned by test/lib/islands/plan-chrome-allowlist.js.
//
// Wired EAGERLY from main.js (beside read-aloud / time-on-task / the tour / no-paste), for the
// same reason as those: its inputs are the server-rendered `#plan-config` JSON and the
// server-rendered mount, so it needs neither the project payload nor `getEditor()` — and on a
// LOCKED page the plan panel is the whole activity, so it must not depend on `loadProject()`.
//
// It imports NO Lit and NO panel code: the `editor` Vite bundle stays Lit-free. The panel is the
// shared LIGHT-DOM `<pc-plan-panel>` (src/islands/shared/pc-plan-panel.ts), loaded on these pages
// as its own shell entry (`/js/shell/pc-plan-panel.js`, a `<script type="module">` the template
// emits only when a plan block exists). This module only creates the element, waits for it to be
// defined, sets its properties and listens to its events.
//
// Absent `#plan-config` => a no-op, so a host without a plan block is unchanged byte for byte.
//
//   LOCKED (a hard-gated block, no submitted plan): the panel is the activity. A successful
//     submit RELOADS the page (the server then renders the unlocked surface). main.js has
//     already mounted the editor read-only and skipped the surface chrome, the close prompt and
//     the tour.
//   UNLOCKED: the plan sits read-only in the rail with "Update my plan" (a `revise` version).
//     `reviseAfterPass`: guided-make — the prompt appears when completion is recorded, through
//     close-prompt.js's one-shot deferral, and the countdown arms only once it is answered;
//     assignment — through the SAME deferral, consulted by the countdown assignment-chrome.js
//     offers after a successful submit; offered directly only when that submit happened while
//     the program was running (`pc-assignment-submitted` `detail.running`, no countdown then).
//     Once per page load, and never when the host was already complete when the page loaded.
//   NARROW: below the container threshold the rail and the code become a Plan | Code tab pair
//     (the CSS lives in the templates; this module owns the tab state on <body>).
//   COMPARE: `body[data-plan-compare]` (the widened rail, side-by-side plans, model open, Plan tab
//     first) applies ONLY on the first load after an unlock — a one-shot sessionStorage flag set
//     just before the unlock reload — and never when the host was already complete on load.

import { setCloseDeferral } from './close-prompt.js';

var ALLOWED_SURFACES = ['guided-make', 'assignment'];
var PANEL_LOAD_MS = 15000;

function readConfig(doc) {
  var el = doc.getElementById('plan-config');
  if (!el) { return null; }
  try {
    var cfg = JSON.parse(el.textContent || 'null');
    // plan-from-link: a READ-ONLY config may carry `plan: null` (its target could not be read —
    // the locked "try again" state), so it is accepted on `readOnly` alone.
    return (cfg && typeof cfg === 'object' && (cfg.plan || cfg.readOnly === true)) ? cfg : null;
  } catch (e) {
    return null;
  }
}

// Exported for main.js: is this page a LOCKED plan page? Either signal is enough — the body
// attribute survives a malformed config block, the config survives a missing attribute.
export function isPlanLocked(doc) {
  try {
    if (doc.body && doc.body.getAttribute('data-plan-locked') === 'true') { return true; }
    var cfg = readConfig(doc);
    return !!(cfg && cfg.locked === true);
  } catch (e) {
    return false;
  }
}

function endpointFor(cfg) {
  return '/api/courses/' + encodeURIComponent(cfg.courseId)
    + '/lessons/' + encodeURIComponent(cfg.lessonId)
    + '/materials/' + encodeURIComponent(cfg.materialId) + '/plan';
}

// Has this student a submitted plan still to compare with the model? A FACT about the server state
// only — on its own it changes nothing on the page (see `shouldCompare`).
export function comparePending(cfg) {
  return !!(cfg && cfg.locked !== true && cfg.submitted === true && cfg.selfCheckDone !== true
    && cfg.planKey && typeof cfg.planKey === 'object');
}

// THE COMPARE STATE IS FIRST-LOAD-ONLY (decisions.md Amendment 4). The rail widens
// (`body[data-plan-compare]`) so the two plans sit side by side, the rail and the model open, and
// at narrow width the Plan tab opens first — but ONLY on the first load right after an unlock
// (a one-shot flag written immediately before the unlock reload and consumed here), and NEVER
// when the host was already complete on load. The self-check is optional on these hosts, so
// re-deriving it from the server config on every load would pin a student who skipped it to a
// 45% rail, a forced-open model and the Plan tab on every later visit.
export function shouldCompare(cfg, flag) {
  return flag === true && comparePending(cfg) && !!cfg && cfg.alreadyComplete !== true;
}

// The one-shot flag's key: per material and per class, so a flag left by one activity is never
// consumed by another.
export function compareFlagKey(cfg) {
  return 'pcplan-compare:' + String((cfg && cfg.materialId) || '') + ':' + String((cfg && cfg.linkedCourseId) || '');
}

// Read AND REMOVE the flag (one-shot). Storage can be missing, throw (private window, blocked site
// data) or come back empty — every case reads as "no flag", which renders the normal page.
export function takeCompareFlag(storage, key) {
  try {
    if (!storage) { return false; }
    var v = storage.getItem(key);
    if (v !== null) { storage.removeItem(key); }
    return v === '1';
  } catch (e) {
    return false;
  }
}

export function setCompareFlag(storage, key) {
  try {
    if (storage) { storage.setItem(key, '1'); }
  } catch (e) { /* no storage: the unlocked page simply opens in its normal layout */ }
}

// Which tab a NARROW page opens on. Plan first while there is no submitted plan — a LOCKED page
// (nothing on the Code side yet) AND a soft / not-retroactive block alike — and on the one
// first-load compare view (`comparing`, from `shouldCompare`); Code first otherwise. A submitted
// plan whose optional self-check was skipped opens on Code on every later visit.
//
// plan-from-link: a READ-ONLY config viewed by staff or a preview copy opens on Code (the panel
// only holds the "Students see their own submitted plan here." line); a student keeps the rule.
export function initialPlanTab(cfg, comparing) {
  if (cfg && cfg.readOnly === true && (cfg.staff === true || cfg.preview === true)) { return 'code'; }
  if (!cfg || cfg.locked === true || cfg.submitted !== true) { return 'plan'; }
  if (comparing === true) { return 'plan'; }
  return 'code';
}

// The revise-after-pass prompt: shown once per page load, and never when the host was already
// complete when the page loaded (or is locked, or has no submitted plan). `offer(show)` calls
// `show()` at most once and only while eligible; `submitted()` records an in-page submit.
export function createReviseGate(cfg) {
  var submitted = !!(cfg && cfg.submitted === true);
  var offered = false;
  function eligible() {
    return !!(cfg && cfg.plan && cfg.plan.reviseAfterPass === true)
      && cfg.locked !== true && cfg.alreadyComplete !== true && submitted && !offered;
  }
  return {
    eligible: eligible,
    submitted: function () { submitted = true; },
    offer: function (show) {
      if (!eligible()) { return null; }
      offered = true;
      return show();
    }
  };
}

function sessionStore() {
  try { return window.sessionStorage || null; } catch (e) { return null; }
}

// The Plan | Code tab pair. The buttons are server-rendered (hidden by CSS above the container
// threshold); this only moves `body[data-plan-tab]` and the ARIA state between them.
function wireTabs(doc, first) {
  var planTab = doc.getElementById('plan-tab-plan');
  var codeTab = doc.getElementById('plan-tab-code');
  if (!planTab || !codeTab) { return; }
  function select(which) {
    doc.body.setAttribute('data-plan-tab', which);
    planTab.setAttribute('aria-selected', which === 'plan' ? 'true' : 'false');
    codeTab.setAttribute('aria-selected', which === 'code' ? 'true' : 'false');
    planTab.tabIndex = which === 'plan' ? 0 : -1;
    codeTab.tabIndex = which === 'code' ? 0 : -1;
  }
  planTab.addEventListener('click', function () { select('plan'); });
  codeTab.addEventListener('click', function () { select('code'); });
  // Arrow keys move between the two tabs (the WAI-ARIA tabs pattern).
  function onKey(ev) {
    if (ev.key !== 'ArrowLeft' && ev.key !== 'ArrowRight') { return; }
    ev.preventDefault();
    var next = (ev.currentTarget === planTab) ? codeTab : planTab;
    select(next === planTab ? 'plan' : 'code');
    next.focus();
  }
  planTab.addEventListener('keydown', onKey);
  codeTab.addEventListener('keydown', onKey);
  select(first);
}

// `whenDefined` never settles if the panel script 404s or throws, so a deadline turns that into a
// visible message instead of a silently empty rail.
function panelDefined() {
  return (window.customElements && typeof window.customElements.whenDefined === 'function')
    ? new Promise(function (resolve, reject) {
      var t = window.setTimeout(function () { reject(new Error('pc-plan-panel did not load')); }, PANEL_LOAD_MS);
      window.customElements.whenDefined('pc-plan-panel').then(function () {
        window.clearTimeout(t);
        resolve();
      });
    })
    : Promise.reject(new Error('custom elements unavailable'));
}

// plan-from-link: the READ-ONLY props a config hands the panel (exported for the host-only spec).
export function readOnlyPanelProps(cfg) {
  return {
      plan           : cfg.plan || null
    , endpoint       : (cfg.courseId && cfg.lessonId && cfg.materialId) ? endpointFor(cfg) : ''
    , linkedCourseId : cfg.linkedCourseId || ''
    , preview        : cfg.preview === true
    , staff          : cfg.staff === true
    , planUrl        : typeof cfg.planUrl === 'string' ? cfg.planUrl : ''
    , variant        : 'host'
    , layout         : 'rail'
    , locked         : cfg.locked === true
    , lockReason     : cfg.lockReason || ''
    , readOnly       : true
    , state          : {
        submitted     : cfg.submitted === true
      , selfCheckDone : cfg.selfCheckDone === true
      , versions      : Array.isArray(cfg.versions) ? cfg.versions : []
    }
  };
}

export function wirePlanChrome(doc) {
  try {
    var surface = doc.body ? (doc.body.getAttribute('data-surface') || 'editor') : 'editor';
    if (ALLOWED_SURFACES.indexOf(surface) === -1) { return null; }
    var cfg = readConfig(doc);
    if (!cfg) { return null; }
    var mount = doc.getElementById('plan-panel-mount');
    if (!mount) {
      console.error('plan-chrome: #plan-panel-mount is missing');
      return null;
    }

    var locked = cfg.locked === true;

    // plan-from-link: the READ-ONLY panel (an assignment whose plan lives in an earlier plan
    // activity). Still only createElement + properties, still the Plan | Code tabs and the rail;
    // NO compare flag, NO reload-on-submit, NO revise gate, NO `pc-assignment-submitted` wiring —
    // the panel cannot write. A locked page unlocks by the panel's own "reload" button.
    if (cfg.readOnly === true) {
      return wireReadOnlyPanel(doc, cfg, mount);
    }

    var flagKey = compareFlagKey(cfg);
    // Consumed on EVERY load (even a locked one), so a stale flag can never fire later.
    var comparing = shouldCompare(cfg, takeCompareFlag(sessionStore(), flagKey));
    wireTabs(doc, initialPlanTab(cfg, comparing));
    if (comparing) { doc.body.setAttribute('data-plan-compare', 'true'); }

    var panel = doc.createElement('pc-plan-panel');
    mount.appendChild(panel);

    // Has THIS student a submitted plan (from the page, or submitted in this page load)? A
    // revise needs one — the server refuses a revise before any submit.
    var revise = createReviseGate(cfg);
    var ready = false;

    var offerRevise = function () {
      if (!ready || typeof panel.openRevisePrompt !== 'function') { return null; }
      return revise.offer(function () {
        return Promise.resolve(panel.openRevisePrompt()).catch(function () { return 'no-changes'; });
      });
    };

    // The comparison is done: the rail goes back to its normal width.
    panel.addEventListener('pc-plan-selfchecked', function () {
      doc.body.removeAttribute('data-plan-compare');
    });

    panel.addEventListener('pc-plan-submitted', function (ev) {
      revise.submitted();
      // Unlock = a full reload: the server re-renders the surface with the student's source. The
      // one-shot flag written first is what makes THAT load (and only that one) the compare view.
      if (locked) {
        setCompareFlag(sessionStore(), flagKey);
        window.location.reload();
        return;
      }
      // A soft / not-retroactive block submitted in place: this IS the comparison moment, so the
      // rail widens now (never persisted — a later load opens in the normal layout).
      var key = ev && ev.detail ? ev.detail.planKey : null;
      if (cfg.alreadyComplete !== true && key && typeof key === 'object') {
        doc.body.setAttribute('data-plan-compare', 'true');
      }
    });

    // guided-make AND assignment (one-tab-topbar, Trello 3h9FqNWJ): hold the close-on-success
    // countdown until the revise prompt is answered. Registered at wire time, only when the
    // prompt could ever show (the same conditions `createReviseGate.eligible` already requires);
    // `offerClose` consumes it once. An `offerRevise()` that returns null (e.g. no submitted plan)
    // returns false, so the countdown arms at once. The assignment's countdown is offered by
    // `assignment-chrome.js` after a successful Submit — so the revise prompt comes first.
    if ((surface === 'guided-make' || surface === 'assignment')
        && cfg.plan && cfg.plan.reviseAfterPass === true
        && !locked && cfg.alreadyComplete !== true) {
      setCloseDeferral(function (proceed) {
        var pending = offerRevise();
        if (!pending) { return false; }
        pending.then(function () { proceed(); }, function () { proceed(); });
        return true;
      });
    }

    // assignment: a successful Submit while the student's program is RUNNING offers no countdown
    // (assignment-chrome.js skips `offerClose` then), so the revise prompt is offered here
    // directly. Otherwise this listener does nothing and the deferral above offers it first. The
    // gate's one-shot `offered` flag guarantees at most one revise offer per page load.
    if (surface === 'assignment') {
      doc.addEventListener('pc-assignment-submitted', function (e) {
        if (e && e.detail && e.detail.running === true) { offerRevise(); }
      });
    }

    return panelDefined().then(function () {
      panel.plan           = cfg.plan;
      panel.endpoint       = endpointFor(cfg);
      panel.linkedCourseId = cfg.linkedCourseId || '';
      panel.preview        = cfg.preview === true;
      panel.variant        = 'host';
      panel.layout         = 'rail';
      panel.locked         = locked;
      panel.lockReason     = cfg.lockReason || '';
      panel.compareFocus   = comparing;
      panel.alreadyComplete = cfg.alreadyComplete === true;
      panel.state          = {
        submitted     : cfg.submitted === true
        , selfCheckDone : cfg.selfCheckDone === true
        , versions      : Array.isArray(cfg.versions) ? cfg.versions : []
        , planKey       : cfg.planKey
      };
      ready = true;
      return panel;
    }).catch(function (err) {
      // The panel script failed to load. Say so in the mount rather than leave an empty rail —
      // on a LOCKED page that would be a dead end with no explanation.
      mount.textContent = 'The plan panel could not load. Reload the page to try again.';
      console.error(err);
      return null;
    });
  } catch (e) {
    // MUST NEVER THROW — it runs beside the other eager wirings in main.js.
    console.error(e);
    return null;
  }
}

// plan-from-link: the READ-ONLY branch of `wirePlanChrome` (reached only AFTER its allow-list
// gate and the `#plan-config` check).
function wireReadOnlyPanel(doc, cfg, mount) {
  wireTabs(doc, initialPlanTab(cfg, false));
  var panel = doc.createElement('pc-plan-panel');
  // Set before the element upgrades too, so the panel never renders (or self-GETs) as editable.
  panel.setAttribute('readonly', '');
  mount.appendChild(panel);
  return panelDefined().then(function () {
    var props = readOnlyPanelProps(cfg);
    Object.keys(props).forEach(function (k) { panel[k] = props[k]; });
    return panel;
  }).catch(function (err) {
    mount.textContent = 'The plan panel could not load. Reload the page to try again.';
    console.error(err);
    return null;
  });
}
